import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Settings2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState } from '@/components/ui/empty-state';

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  /** Stable row id (defaults to `row.id` when present, else the index). */
  getRowId?: (row: TData, index: number) => string;
  isLoading?: boolean;
  /** Navigate here when a row is clicked (Enter/Space on the focused row too). */
  rowHref?: (row: TData) => string;
  onRowClick?: (row: TData) => void;
  /** Sorting can be controlled (saved views) or left internal. */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  /** Global text filter (client side). Controlled or internal. */
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  /** Hide the built-in search box (when a FilterBar provides `text`). */
  hideSearch?: boolean;
  searchPlaceholder?: string;
  /** Extra toolbar content (FilterBar, ViewSwitcher, buttons). */
  toolbar?: React.ReactNode;
  /** Pagination. Client side by default; pass `manualPagination` + `rowCount` for server paging. */
  pageSize?: number;
  pageSizeOptions?: number[];
  manualPagination?: boolean;
  rowCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  hidePagination?: boolean;
  emptyTitle?: React.ReactNode;
  emptyDescription?: React.ReactNode;
  /** Max height of the scrolling area (the header stays sticky inside it). */
  maxHeight?: string;
  className?: string;
  /** Caption for screen readers. */
  caption?: string;
}

function defaultGetRowId<TData>(row: TData, index: number): string {
  const id = (row as { id?: unknown }).id;
  return typeof id === 'string' || typeof id === 'number' ? String(id) : String(index);
}

/**
 * Generic table on TanStack Table v8: sorting, column visibility chooser, global text filter,
 * row-click navigation, pagination, sticky header, horizontal scroll on small screens.
 *
 * Columns are plain `ColumnDef`s; give sortable columns an `id`/`accessorKey` and a string
 * `header` (the sort button is rendered for you). Use `meta: { align: 'right' }` for numbers.
 */
export function DataTable<TData>({
  columns,
  data,
  getRowId = defaultGetRowId,
  isLoading = false,
  rowHref,
  onRowClick,
  sorting: sortingProp,
  onSortingChange,
  columnVisibility: visibilityProp,
  onColumnVisibilityChange,
  columnFilters: columnFiltersProp,
  onColumnFiltersChange,
  globalFilter: globalFilterProp,
  onGlobalFilterChange,
  hideSearch = false,
  searchPlaceholder = 'Filter rows…',
  toolbar,
  pageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  manualPagination = false,
  rowCount,
  pagination: paginationProp,
  onPaginationChange,
  hidePagination = false,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  maxHeight,
  className,
  caption,
}: DataTableProps<TData>) {
  const navigate = useNavigate();
  const [sortingState, setSortingState] = React.useState<SortingState>([]);
  const [visibilityState, setVisibilityState] = React.useState<VisibilityState>({});
  const [columnFiltersState, setColumnFiltersState] = React.useState<ColumnFiltersState>([]);
  const [globalFilterState, setGlobalFilterState] = React.useState('');
  const [paginationState, setPaginationState] = React.useState<PaginationState>({ pageIndex: 0, pageSize });

  const sorting = sortingProp ?? sortingState;
  const columnVisibility = visibilityProp ?? visibilityState;
  const columnFilters = columnFiltersProp ?? columnFiltersState;
  const globalFilter = globalFilterProp ?? globalFilterState;
  const pagination = paginationProp ?? paginationState;

  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: { sorting, columnVisibility, columnFilters, globalFilter, pagination },
    onSortingChange: onSortingChange ?? setSortingState,
    onColumnVisibilityChange: onColumnVisibilityChange ?? setVisibilityState,
    onColumnFiltersChange: onColumnFiltersChange ?? setColumnFiltersState,
    onGlobalFilterChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? (updater as (old: string) => string)(globalFilter)
          : (updater as string);
      if (onGlobalFilterChange) onGlobalFilterChange(next ?? '');
      else setGlobalFilterState(next ?? '');
    },
    onPaginationChange: onPaginationChange ?? setPaginationState,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    manualPagination,
    rowCount: manualPagination ? rowCount : undefined,
    globalFilterFn: 'includesString',
    autoResetPageIndex: false,
  });

  const activate = (row: Row<TData>) => {
    if (onRowClick) onRowClick(row.original);
    if (rowHref) navigate(rowHref(row.original));
  };
  const interactive = Boolean(onRowClick || rowHref);
  const rows = table.getRowModel().rows;
  const total = manualPagination ? (rowCount ?? data.length) : table.getFilteredRowModel().rows.length;
  const pageStart = total === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const pageEnd = Math.min(total, (pagination.pageIndex + 1) * pagination.pageSize);
  const hideableColumns = table.getAllLeafColumns().filter((c) => c.getCanHide());

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        {hideSearch ? null : (
          <Input
            type="search"
            value={globalFilter}
            onChange={(e) => table.setGlobalFilter(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label="Filter rows"
            className="h-9 w-full sm:w-64"
          />
        )}
        {toolbar}
        <div className="ml-auto flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" aria-label="Choose columns">
                <Settings2 aria-hidden="true" />
                <span className="hidden sm:inline">Columns</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {hideableColumns.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={column.getIsVisible()}
                  onCheckedChange={(v) => column.toggleVisibility(!!v)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-lg border">
        <div className="scrollbar-thin w-full overflow-auto" style={maxHeight ? { maxHeight } : undefined}>
          <Table>
            {caption ? <caption className="sr-only">{caption}</caption> : null}
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    const align = (
                      header.column.columnDef.meta as { align?: 'left' | 'right' | 'center' } | undefined
                    )?.align;
                    return (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                        className={cn(align === 'right' && 'text-right', align === 'center' && 'text-center')}
                        aria-sort={
                          sorted === 'asc'
                            ? 'ascending'
                            : sorted === 'desc'
                              ? 'descending'
                              : canSort
                                ? 'none'
                                : undefined
                        }
                      >
                        {header.isPlaceholder ? null : canSort ? (
                          <button
                            type="button"
                            className={cn(
                              '-ml-2 inline-flex h-8 items-center gap-1 rounded px-2 hover:bg-accent hover:text-accent-foreground',
                              align === 'right' && '-mr-2 ml-0 flex-row-reverse',
                            )}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {sorted === 'asc' ? (
                              <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                            ) : sorted === 'desc' ? (
                              <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                            ) : (
                              <ArrowUpDown className="h-3.5 w-3.5 opacity-40" aria-hidden="true" />
                            )}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {table.getVisibleLeafColumns().map((c) => (
                      <TableCell key={c.id}>
                        <Skeleton className="h-4 w-full max-w-[12rem]" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={Math.max(1, table.getVisibleLeafColumns().length)} className="p-6">
                    <EmptyState
                      compact
                      title={emptyTitle}
                      description={emptyDescription}
                      className="border-0"
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                    tabIndex={interactive ? 0 : undefined}
                    onClick={interactive ? () => activate(row) : undefined}
                    onKeyDown={
                      interactive
                        ? (e) => {
                            if (e.target !== e.currentTarget) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              activate(row);
                            }
                          }
                        : undefined
                    }
                    className={cn(
                      interactive && 'cursor-pointer focus-visible:bg-muted/60 focus-visible:outline-none',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const align = (
                        cell.column.columnDef.meta as { align?: 'left' | 'right' | 'center' } | undefined
                      )?.align;
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            align === 'right' && 'text-right tabular-nums',
                            align === 'center' && 'text-center',
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {hidePagination ? null : (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <div aria-live="polite">{total === 0 ? 'No rows' : `${pageStart}–${pageEnd} of ${total}`}</div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <span className="hidden sm:inline">Rows per page</span>
              <Select value={String(pagination.pageSize)} onValueChange={(v) => table.setPageSize(Number(v))}>
                <SelectTrigger className="h-8 w-[4.5rem]" aria-label="Rows per page">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent side="top">
                  {pageSizeOptions.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                aria-label="First page"
              >
                <ChevronsLeft aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                aria-label="Previous page"
              >
                <ChevronLeft aria-hidden="true" />
              </Button>
              <span className="px-2 tabular-nums">
                {pagination.pageIndex + 1} / {Math.max(1, table.getPageCount())}
              </span>
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                aria-label="Next page"
              >
                <ChevronRight aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                aria-label="Last page"
              >
                <ChevronsRight aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
